import { _longPressTypes, LPW } from 'global/wrappers/LongPressWrapper';
import _ from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Card, Icon, Label } from 'semantic-ui-react';
import { ElementContext } from '../../../cnr/contexts/ElementContext';
import { PageAreaParentContext } from '../../../cnr/contexts/PageAreaParentContext';
import { UiItemContext } from '../../../cnr/contexts/UiItemContext';
import { uniqueKey } from '../../../common/keys';
import { appIconTypes } from '../../../enums/appIconTypes';
import { gEnums } from '../../../enums/globalEnums';
import { _animateTypes } from '../../../motions/AnimateComponent';
import TransitionWrapper, { transitionWrapperTypes } from '../../../wrappers/TransitionWrapper';
import UiImage from '../../imaging/UiImage';
import { propsPicker } from '../../props/propsPicker';
import { useElementOnScreen } from '../visibleElements';
import CardIndicator from './CardIndicator';
import { useCardElements } from 'global/cnr/contexts/useHelpers/useCardElements';

const CardAuthIcon = React.lazy(() => import('./CardAuthIcon'));
const PdfGallery = React.lazy(() => import('../../imaging/PdfGallery'));
const PropSorter = React.lazy(() => import('../../../dragNDrops/PropSorter'));
const QrCode = React.lazy(() => import('../../../pageItem/modification/pageItemActions/QrCode'));
const StarRating = React.lazy(() => import('../../ratings/StarRating'));
const UiCredential = React.lazy(() => import('../../../pageItem/modification/dataModifications/UiCredential'));

const CardProfile = () => {

  const _cds = gEnums.cardDisplayTypes

  const { getCardElements } = useCardElements()

  // parentContext
  const parentContext = useContext(PageAreaParentContext)
  const { states, handlers, fns } = parentContext ?? {}
  const { appSettings_state, eventInfo_state, currentSettings_state, framework_state, rating_state, transition_state } = states
  const { settingsViewer_handlers } = handlers
  const { appUser_fns } = fns ?? {}
  const { pagePropsSettingsOn } = appSettings_state ?? {}
  const { staticViews, staticViews_gs } = eventInfo_state ?? {}
  const { aps_global } = _.get(currentSettings_state, 'gvviSettings', {});
  const { appCredentialing, appRating } = aps_global ?? {}
  const { showEnteredInApp } = appCredentialing ?? {}
  const { authOn } = appSettings_state ?? {}
  const { ratings } = rating_state ?? {}

  const _staticViews = staticViews_gs ? staticViews_gs : staticViews

  const { propMode } = framework_state ?? {}

  // transitionContext  
  const { transitions } = transition_state ?? {}
  const transition = transitions ? transitions[_animateTypes.pageItemElement] : null
  const { showTransition } = transition ?? {}

  // uiItemContext 
  const uiItemContext = useContext(UiItemContext)
  const { item_state } = uiItemContext ?? {}
  const { _display, viewItem, _imageOpts } = item_state ?? {}

  // elementContext
  const elementContext = useContext(ElementContext)
  const { element_state } = elementContext ?? {}
  const { itemData, elemProps, clickTo } = element_state ?? {}
  const { viewItem: viewItem_direct, handleClick, showPdfs, forAppManagement, imageOptions } = elemProps ?? {}
  const { email, fcmTokens, phoneNumber, _itemKey } = itemData ?? {}

  const currentRating = ratings && ratings[_itemKey]

  // _viewItem depending whether viewItem_direct exists
  const _viewItem = viewItem_direct ? viewItem_direct : viewItem
  const { display: display_direct } = viewItem_direct ?? {}
  const display_final = display_direct ? display_direct : _display

  const { showCaptionInitial, sortProp } = display_final ?? {}
  let { cardDisplayType } = display_final ?? {}
  const { qrCodes, props: props_viewItem, key: key_viewItem } = _viewItem ?? {}
  const { showItemImage, imageLocation, imageSize } = _imageOpts ?? {}
  const { showQrCode } = qrCodes ?? {}

  const rating_access = appUser_fns.validateAccess_rating(currentRating, appRating, key_viewItem)
  const { allowAppUserView } = rating_access ?? {}

  const _cardKeyy = uniqueKey('cpr', _itemKey)

  let imageInContent = true

  switch (imageLocation) {
    case gEnums.imageLocations.top:
      imageInContent = false
      break;
    default:
    // nothing
  }

  const [containerRef, isVisible] = useElementOnScreen({ root: null, rootMargin: '0px', threshold: 1.0 })

  const [nameElements, setNameElements] = useState()
  const [metaElements, setMetaElements] = useState()
  const [propSections, setPropSections] = useState()
  const [propIndicators, setPropIndicators] = useState()

  const sectionGroupedElements = useMemo(() => {
    return getCardElements({ _keyy: _cardKeyy, itemData })
  }, [itemData])

  // change the state elements
  useEffect(() => {
    const {
      nameElements: _nameElements,
      metaElements: _metaElements,
      propSections: _propSections
    } = sectionGroupedElements

    if (showCaptionInitial && sortProp && itemData[sortProp]) {
      _nameElements.unshift(<Label key={uniqueKey('prcr', 'l', 'ci')} circular color='blue'>{itemData[sortProp].substring(0, 1)}</Label>)
    }

    if (authOn) { _metaElements.push(<CardAuthIcon email={email} />) }

    const _propIndicators = []
    const _indicators = _.filter(props_viewItem, i => i.indicator && i.indicator.showIndicator);

    _indicators.forEach(ind => {
      if (itemData && ind.indicator && itemData[ind.key]) {
        _propIndicators.push({ ...ind.indicator, indicatorProp: ind.key })
      }
    })

    setPropIndicators(_propIndicators)

    if (settingsViewer_handlers && propMode) {
      setNameElements(propsPicker({ items: _nameElements, settingsViewer_handlers }))
      setMetaElements(propsPicker({ items: _metaElements, settingsViewer_handlers }))
      setPropSections(propsPicker({ items: _propSections, settingsViewer_handlers }))
    } else {
      setNameElements(_nameElements)
      setMetaElements(_metaElements)
      setPropSections(_propSections)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [sectionGroupedElements]);

  const handleItemClick = () => {
    handleClick && handleClick(_viewItem, _itemKey, null, { itemData: itemData, clickTo })
  }

  let cn = 'card-profile'
  let showFullImage = false

  if (showQrCode) { cn += ' card-qr' }

  if ((propIndicators && propIndicators.length > 0) || showEnteredInApp) { cn += ' indic' }
  if (showPdfs) { cn += ' pdf' }

  switch (cardDisplayType) {
    case _cds.profilePics:
      cn += ' pci'
      break;

    case _cds.profilePlus:
    case _cds.schedulePlus:
      cn += ' plus'
      showFullImage = true
      break;
    default:
    // nothing
  }

  const cardImage_normal = () => <UiImage
    viewItem={_viewItem}
    itemData={itemData}
    showFullImage={showFullImage}
    imageSize={imageSize}
    imageOptions={imageOptions}
    cardDisplayType={cardDisplayType}
  />

  const cardQr = () => <QrCode
    key={uniqueKey('cp', 'qr', _itemKey)}
    itemData={_viewItem}
    viewItem={_viewItem}
    qrCodeType={gEnums.qrCodeTypes.itemData}
  />

  const cardIcons = () => <div key={uniqueKey('cp', 'd', _cardKeyy)} >
    <Icon key={uniqueKey('cp', 'i', _cardKeyy, 1)} bordered name={appIconTypes.mail} color={email ? 'blue' : 'grey'} />
    <Icon key={uniqueKey('cp', 'i', _cardKeyy, 2)} bordered name={appIconTypes.notifications} color={fcmTokens ? 'blue' : 'grey'} />
    <Icon key={uniqueKey('cp', 'i', _cardKeyy, 3)} bordered name={appIconTypes.phone} color={phoneNumber ? 'blue' : 'grey'} />
  </div>

  const cardIndicator = (ind) => <CardIndicator
    indicator={ind}
    cardKey={_cardKeyy}
    itemData={itemData}
    aps_global={aps_global}
    staticViews={_staticViews}
  />

  const cardIndicators = () => <div className='card-indics' key={uniqueKey('cp', 'ci', _cardKeyy)}>
    {propIndicators.map(ind => cardIndicator(ind))}
  </div>

  const cardPdfs = () => <div className='card-pdf' key={uniqueKey('cp', 'ci', _cardKeyy)}>
    <PdfGallery />
  </div>

  const cardContent = () => <React.Fragment key={uniqueKey('cc', 'rf')}>
    {!imageInContent && cardImage_normal()}
    <Card.Content key={uniqueKey('cp', 'crd', _cardKeyy)} className='card-content'>
      <Card.Header key={uniqueKey('cp', 'crd', 'h', _cardKeyy)}>
        {showItemImage && imageInContent && (imageLocation === gEnums.imageLocations.inline) && cardImage_normal()}
        {nameElements}
      </Card.Header>
      <Card.Meta key={uniqueKey('cp', 'mta', _cardKeyy)}>{metaElements}</Card.Meta>
      {allowAppUserView &&
        <Card.Meta key={uniqueKey('cs', 'sm', _cardKeyy)}>
          <StarRating key={uniqueKey('ss', 'sr', _cardKeyy)} allowRating={true} itemData={itemData} key_viewItem={key_viewItem} />
        </Card.Meta>
      }
    </Card.Content>
    {showItemImage && (imageInContent && imageLocation !== gEnums.imageLocations.inline) && <Card.Content key={uniqueKey('cp', 'crd', _cardKeyy, 1)} className={'card-image prof'}>{cardImage_normal(true)}</Card.Content>}
    {showQrCode && <Card.Content key={uniqueKey('cp', 'crd', _cardKeyy, 2)} className={'card-qr'}>{cardQr()}</Card.Content>}
    {forAppManagement && <Card.Content key={uniqueKey('cp', 'crd', _cardKeyy, 3)} extra className={'card-icons'}>{cardIcons()}</Card.Content>}
    {propIndicators && propIndicators.length > 0 && cardIndicators()}
    {showPdfs && cardPdfs()}
    {showEnteredInApp && <UiCredential cardKey={_cardKeyy} itemData={itemData} />}
  </React.Fragment>

  const profilePic = () => <div key={uniqueKey('cp', 'img', _cardKeyy)} className='pic-profile' onClick={() => { handleItemClick() }}>
    {cardImage_normal()}
    <div>{nameElements}</div>
  </div>

  const cardProfile = (contentOnly) => {
    switch (cardDisplayType) {
      case _cds.profilePics:
        return profilePic()
      default:
        if (contentOnly) {
          return cardContent()
        } else {
          return <LPW
            itemData={itemData}
            hasImage={showItemImage}
            longPressType={_longPressTypes.item}>
            {/* <DroppableItem allowDrop={false} mode={_dragModes.solo}> */}
            {/* <div>CARD</div> */}
            {/* <div className='crdd'>
              {cardContent()}
            </div> */}
            <Card fluid className={cn} key={uniqueKey('cp', _cardKeyy)} as='div' onClick={() => { handleItemClick() }}>
              {cardContent()}
            </Card>
            {/* </DroppableItem> */}
          </LPW>
        }
    }
  }

  const _showTransition = showTransition && cardDisplayType.indexOf('Plus') < 0

  const normalContent = () => {
    if (_showTransition) {
      return <TransitionWrapper transitionWrapperType={transitionWrapperTypes.simpleFade} useMulti={true}>
        {cardProfile()}
      </TransitionWrapper>
    } else {
      return cardProfile()
    }
  }

  return pagePropsSettingsOn ? <PropSorter propSections={propSections} viewItem={_viewItem} /> : normalContent()
}

export default CardProfile